/**
Note regarding this file's name:

Usually plugins factories are in the index.js file of their corresponding folder.
This used to be the case. However this file was also being used to host the logManager
that is being used with the SDK.

Since the logManager is being included in almost every file it was preferable to rename this
file rather than point 100s of files to a new place for the logManager.
**/

// Logs plugin.
import api from './interface/api'
import reducer from './interface/reducers'
import * as actions from './interface/actions'

import { defaultOptions, partialDefaultLogActions /*,  parseLogConfig */ } from './config'
import { setLevelEntry, setHandlerEntry, getLevelMap } from './sagas'
import createActionLogger from './actions'

// Other plugins.
import { update as updateConfig } from '../config/interface/actions'
import { mergeValues } from '../common/utils'

// Libraries.
import { put } from 'redux-saga/effects'
import { logLevels } from 'kandy-logger'
import { logManager, API_LOG_TAG } from './index'

// Include the extra JSDoc items.
import './docs'

export default function logPlugin (options = {}, bottle) {
  const name = 'logs'
  // Register the logManager with the bottle so that other plugins have access to it.
  bottle.value('logManager', logManager)
  bottle.constant('API_LOG_TAG', API_LOG_TAG)

  const logger = logManager.getLogger('LOGS')

  // Make sure the configured log handler was a function.
  if (options.handler && typeof options.handler !== 'function') {
    delete options.handler
    logger.warn('Invalid log handler configuration provided; using default instead.')
  }

  // Make sure configured log level is supported.
  if (options.logLevel && !Object.keys(logLevels).includes(options.logLevel.toUpperCase())) {
    delete options.logLevel
    logger.warn('Invalid log level configuration provided; using default instead.')
  }

  options = mergeValues(defaultOptions, options)

  // Check if `logActions` is true and apply defaults for action logging
  if (options.logActions !== false) {
    if (options.logActions instanceof Object) {
      options.logActions = mergeValues(partialDefaultLogActions, options.logActions)
    } else {
      options.logActions = partialDefaultLogActions
    }
  }

  // Now that we have the application's log configs, update everything to
  //    use those values instead of default values.
  logManager.setLevel(options.logLevel)
  if (options.handler) {
    logManager.setHandler(options.handler)
  }

  Object.values(logManager.getLoggers()).forEach(logger => {
    logger.setLevel(options.logLevel)
    if (options.handler) {
      logger.setHandler(options.handler)
    }
  })

  function * init ({ webRTC }) {
    // Send the provided options to the store.
    // This will be `state.config[name]`.
    yield put(updateConfig(options, name))
    // Update state with the initial Logger levels.
    yield put(actions.levelsChanged(getLevelMap(logManager)))

    // Ensure the webRTC object exists. It won't in 3.X.
    if (webRTC) {
      // Update everything to
      //    use those values from the application configs instead of default values in the webrtc Stack.
      const webRTCLogManager = webRTC.managers.logs
      if (options.handler) {
        webRTCLogManager.setHandler(options.handler)
      }
      Object.values(webRTCLogManager.getLoggers()).forEach(logger => {
        logger.setLevel(options.logLevel)
        if (options.handler) {
          logger.setHandler(options.handler)
        }
      })
    }
  }

  const components = {
    name,
    capabilities: ['logs'],
    init,
    api,
    reducer,
    sagas: [setLevelEntry, setHandlerEntry]
  }

  options.logLevel = options.logLevel.toUpperCase()
  const setLevel = logLevels[options.logLevel]
  // Consider actions to be at the INFO log level.
  // Only export a middleware (for actions) at the appropriate levels.
  if (setLevel <= logLevels.INFO && options.logActions !== false) {
    components.middleware = createActionLogger(options)
  }

  return components
}
